<template>
  <div class="purchase">
    <div class="purchase__top mb-1 mb-md-1-5 mb-xlg-2">
      <h1 class="text-md-center">{{ productDocument.title }}</h1>

      <color-picker
        v-if="colorsDimension.length > 0"
        id="colorPicker"
        v-model="selectedDimensions.color"
        :colors="colorsDimension"
        class="d-md-none"
        :skeleton="loading"
        @input="onColorChange"
      />

      <product-price
        v-if="selectedJacket || selectedPant"
        :price="price"
        :compare-at-price="compareAtPrice"
        :currency="shopifyProduct.currency"
        :class="withTestimonials ? 'mb-0-4' : 'mb-1'"
      />
      <product-price
        v-else
        :price="price"
        :compare-at-price="compareAtPrice"
        :variant="shopifyProduct.availble"
        :currency="shopifyProduct.currency"
        :class="withTestimonials ? 'mb-0-4' : 'mb-1'"
      />

      <purchase-testimonials
        v-if="withTestimonials"
        :number-of="productDocument.rating_number"
        :rating="productDocument.rating_average"
        class="mb-0-4"
      />

      <div class="d-flex justify-center text-left mb-0">
        <div>
          <ul class="purchase__bullets mb-0">
            <li
              v-for="el in productDocument.bullets"
              :key="el"
              class="text-small"
              data-cy-bullet-el
            >
              {{ el }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <color-picker
      v-if="colorsDimension.length > 0"
      id="colorPicker"
      v-model="selectedDimensions.color"
      :colors="colorsDimension"
      class="d-none d-md-block"
      :skeleton="loading"
      @input="onColorChange"
    />

    <transition name="fade">
      <div
        v-if="hasSizePicker"
        key="size-picker"
        class="purchase__size-picker mb-1"
      >
        <value-picker
          id="jacket"
          v-model="selectedDimensions.jacket"
          :values="jacketsDimension"
          :label="$t('purchase.label.jacket')"
          :skeleton="loading"
          none
          @input="updateState(true, 'jacket')"
        />

        <value-picker
          id="pant"
          v-model="selectedDimensions.pant"
          :values="pantsDimension"
          :label="$t('purchase.label.pant')"
          :skeleton="loading"
          none
          class="mt-1"
          @input="updateState(true, 'pant')"
        />
      </div>
    </transition>

    <div
      v-if="productDocument.size_guide"
      ref="sizeGuideLinks"
      class="d-flex align-center justify-center gap-1-5"
      :class="{
        'size-guide-highlighted': productDocument.highlight_size_guide,
      }"
    >
      <a
        :href="`#${$t('purchase.size-guide')}`"
        class="link-cta link--size-guide lh-1"
        @click.prevent="sizeGuide()"
      >
        <ceintre-icon />
        <span>{{ $t('purchase.size-guide') }}</span>
      </a>

      <!-- <client-only>
        <div class="d-flex" @click="trackFitle" v-html="fitleTag" />
      </client-only> -->
    </div>

    <div class="purchase__bottom mt-2 mt-xlg-3">
      <transition name="fade" mode="out-in">
        <div v-if="productIsOnSale && !soldOut" key="buy">
          <primary-button
            id="AddToCart"
            :text="actionText"
            align="center"
            with-arrow
            type="button"
            :loading="cartLoading"
            :skeleton="!refresh"
            class="mb-1"
            @click="addToCart"
          ></primary-button>

          <div
            v-if="productDocument.type !== TYPES.GIFTCARD"
            class="purchase__delivery text-small text-center mb-1-5"
          >
            <span v-if="productStatus === STATUS.PRECOMMANDE">
              {{ $t('purchase.estimatedDelivery') }}
              <span
                v-if="!refresh"
                class="skeleton skeleton--bar d-inline-block"
                style="width: 100px; vertical-align: middle"
                >&nbsp;</span
              >
              <span v-else
                >{{ $t(deliveryText.ref, deliveryText.options)
                }}<span v-if="deliveryText.ref === 'noDays'">{{
                  $t('purchase.contactCare')
                }}</span></span
              >
            </span>
            <span v-else>
              {{ $t('purchase.delivery') }}&nbsp;{{ $t('purchase.fewDays') }}
            </span>
          </div>

          <div
            v-if="productStatus === STATUS.PRECOMMANDE"
            class="purchase__informations row row-hard"
          >
            <div
              v-if="showTimer"
              class="text-center"
              :class="{
                'col-xs-6': productDocument.type !== TYPES.ETERNEL,
              }"
            >
              <strong
                v-if="!refresh"
                class="skeleton skeleton--bar d-block"
                style="width: 100px; margin: 0 auto"
                >&nbsp;</strong
              >
              <client-only v-else>
                <count-down
                  :end="productDocument.dates.end"
                  :over-text="$t('purchase.timer.over')"
                  class="text-big w-600"
                >
                  <div class="text-caption text-small w-400 lh-1-2 d-block">
                    {{ $t('purchase.precoEnd') }}
                  </div>
                </count-down>
              </client-only>
            </div>
            <div
              class="text-center"
              :class="
                productDocument.type === TYPES.ETERNEL
                  ? 'col-xs-12 text-center'
                  : 'col-xs-6'
              "
            >
              <div class="text-big mb-0-5">
                <strong
                  v-if="!refresh"
                  class="skeleton skeleton--bar d-block"
                  style="width: 100px; margin: 0 auto"
                  >&nbsp;</strong
                >

                <number-animated
                  v-else
                  ref="number"
                  :from="numberOfSells.from"
                  :to="numberOfSells.to"
                  :duration="0.5"
                  class="w-600"
                  @complete="numberOfSells.from = numberOfSells.to"
                />
              </div>
              <div class="text-caption lh-1-2 d-block">
                {{ $t('purchase.nbPreco') }}
              </div>
            </div>
          </div>

          <a
            v-if="
              productDocument.delivery_and_returns &&
              productDocument.delivery_and_returns._ref
            "
            href="#delivery-and-exchange"
            class="purchase__delivery link-cta mt-2"
            @click.prevent="exchange()"
          >
            <box-icon />
            <span>{{ $t('purchase.exchange') }}</span>
          </a>
        </div>

        <div
          v-else
          key="create-alert"
          class="purchase__create-alert text-center"
        >
          <create-alert
            :product="bisProduct"
            :title="createAlertTitle"
            :text="createAlertText"
            :date="createAlertDate"
            :origin="ORIGIN.PRODUCT"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { differenceInDays, isPast } from 'date-fns'
import slugify from 'slugify'

import ProductPrice from '@/components/Product/ProductPrice'
import PrimaryButton from '@/components/buttons/Primary'
import ColorPicker from '@/components/picker/ColorPicker'
import ValuePicker from '@/components/picker/ValuePicker'
import PurchaseTestimonials from '@/components/Purchase/PurchaseTestimonials'
import CreateAlert from '@/components/Product/CreateAlert'
import BoxIcon from '@/components/icons/Box'
import CeintreIcon from '@/components/icons/Ceintre'

import DeliveryMixin from '@/mixins/deliveryText'

import { STATUS, ORIGIN, SHOPIFY_TYPES, TYPES, SS_UPSELLS_ITEMS } from '@/const'
import { VARIANT_TYPE } from '@/entities/shopify/Product'

import ShopifyBase64 from '@/utils/shopifyBase64'
import { EVENTS } from '@/const/events'

import replaceUids from '@/services/utils/replaceUids'
import {
  isProductSaleable,
  isProductWillSoonBeForSale,
  PRODUCT_COLOR_STATUS,
} from '~/entities/sanity/product'
import { getSanityBunnyImageLink } from '~/utils/image'

export default {
  name: 'PurchaseSuit',
  components: {
    ProductPrice,
    PrimaryButton,
    ColorPicker,
    ValuePicker,
    CreateAlert,
    PurchaseTestimonials,
    BoxIcon,
    CeintreIcon,

    CountDown: () => ({
      component: import(
        '@/components/countdown/CountDown' /* webpackChunkName: 'countdown' */
      ),
    }),
    NumberAnimated: () => ({
      component: import(
        '@/components/number/Number' /* webpackChunkName: 'animated_number' */
      ),
    }),
  },
  mixins: [DeliveryMixin],
  props: {
    productDocument: { type: Object, required: true },
    shopifyProduct: { type: Object, required: true },
    careGuide: { type: String, default: '' },
    withSizeGuide: { type: Boolean, default: false },
    withTestimonials: { type: Boolean, default: false },
    materials: { type: Array, default: () => [] },
    productVariantUpdated: { type: Boolean, required: true },
  },
  data: () => ({
    selectedDimensions: {
      color: '',
      material: '',
      jacket: '',
      pant: '',
    },
    loading: true,
    refresh: false,
    numberOfSells: { from: 0, to: 0 },
    STATUS,
    ORIGIN,
    TYPES,
  }),
  computed: {
    ...mapGetters({
      cart: 'cart/cart',
      cartLoading: 'cart/isLoading',
    }),

    productIsOnSale() {
      if (this.shopifyProduct.hasDates) {
        return this.shopifyProduct.isSaleableAsStock
      }

      return isProductSaleable(this.productDocument)
    },

    productStatus() {
      if (this.shopifyProduct.hasDates) {
        return this.shopifyProduct.isSaleableAsStock
          ? STATUS.STOCK
          : STATUS.AGENDA
      }

      return this.productDocument.status
    },

    bisProduct() {
      return {
        productSlug: this.productDocument.uid,
        productName: this.productDocument.title,
        productId: this.shopifyProduct.id,
      }
    },
    hasSizePicker() {
      return (
        this.productIsOnSale &&
        this.shopifyProduct.isDimensionAvailable(
          'color',
          this.selectedDimensions.color
        )
      )
    },
    price() {
      if (this.selectedJacket && this.selectedPant) {
        return this.selectedJacket.price + this.selectedPant.price
      }

      if (this.selectedJacket) {
        return this.selectedJacket.price
      }

      if (this.selectedPant) {
        return this.selectedPant.price
      }

      return this.shopifyProduct.price
    },
    compareAtPrice() {
      if (this.selectedJacket && this.selectedPant) {
        return (
          this.selectedJacket.compareAtPrice + this.selectedPant.compareAtPrice
        )
      }

      if (this.selectedJacket) {
        return this.selectedJacket.compareAtPrice
      }

      if (this.selectedPant) {
        return this.selectedPant.compareAtPrice
      }

      return this.shopifyProduct.compareAtPrice
    },
    isInPreOrder() {
      if (this.selectedJacket) {
        return this.selectedJacket.price !== this.selectedJacket.compareAtPrice
      }

      if (this.selectedPant) {
        return this.selectedPant.price !== this.selectedPant.compareAtPrice
      }

      return false
    },
    actionText() {
      if (this.productStatus === STATUS.STOCK) {
        return this.$t('action.buy')
      }

      return this.$t('action.preorder')
    },
    createAlertText() {
      if (this.onSale && this.soldOut) {
        return this.$t('product.soldout')
      }

      if (this.isInPreOrder) {
        return this.$t('product.receive-alert-preorder')
      }

      return this.$t('product.receive-alert-stock')
    },
    createAlertTitle() {
      if (this.onSale) {
        if (!this.selectedColor.available) {
          return this.$t('product.color-not-available')
        }

        if (this.soldOut) {
          return this.$t('product.size-not-available')
        }
      }

      return this.$t('product.back-one-day')
    },
    createAlertDate() {
      if (!this.productIsOnSale) {
        const now = new Date()

        if (this.shopifyProduct.startDate) {
          return this.shopifyProduct.startDate > now
            ? this.shopifyProduct.startDate
            : false
        }

        if (isProductWillSoonBeForSale(this.productDocument)) {
          return this.productDocument.dates.start
        }
      }

      return false
    },
    delivery() {
      return this.shopifyProduct.metafield
        ? new Date(this.shopifyProduct.metafield)
        : undefined
    },
    deliveryText() {
      if (
        this.productStatus === STATUS.PRECOMMANDE &&
        this.delivery &&
        !isNaN(this.delivery.getTime())
      ) {
        return this.getDeliveryFromDate(this.delivery)
      }
      if (this.productStatus === STATUS.STOCK) {
        return { ref: 'purchase.fewDays', options: {} }
      }

      return { ref: 'purchase.noDays', options: {} }
    },
    colorsDimension() {
      const cc = []

      for (const color of this.productDocument.colors) {
        const colorKey = this.getColorFromColorKey(color._key)
        if (this.shopifyProduct.isDimensionHasValue('color', colorKey)) {
          const label =
            this.productDocument.shopify_product.colorOptions.find(
              (colorOption) => colorOption._key === colorKey
            )?.name || color.label

          cc.push({
            value: colorKey,
            label,
            isNew: color.color_status === PRODUCT_COLOR_STATUS.NEW,
            isLeavingSoon:
              color.color_status === PRODUCT_COLOR_STATUS.LEAVING_SOON,
            image: {
              url: getSanityBunnyImageLink(color.thumbnail, {
                width: 100,
                height: 100,
              }),
              dimensions: {
                width: 100,
                height: 100,
              },
            },
            available: this.shopifyProduct.isDimensionAvailable(
              'color',
              colorKey
            ),
            materials: this.shopifyProduct.getDimensionsByDimension(
              'color',
              colorKey,
              'material'
            ),
          })
        }
      }

      return cc
    },
    jacketsDimension() {
      if (this.selectedDimensions.color !== '') {
        return this.shopifyProduct.filterVariantsByDimensions([
          { dimension: 'color', value: this.selectedDimensions.color },
          { dimension: 'group', value: VARIANT_TYPE.JACKET },
        ])
      }

      return []
    },
    pantsDimension() {
      if (this.selectedDimensions.color !== '') {
        return this.shopifyProduct.filterVariantsByDimensions([
          { dimension: 'color', value: this.selectedDimensions.color },
          { dimension: 'group', value: VARIANT_TYPE.PANT },
        ])
      }

      return []
    },
    selectedJacket() {
      return this.shopifyProduct.variants.find((v) => {
        return (
          v.opt.group.label === VARIANT_TYPE.JACKET &&
          v.opt.color.value === this.selectedDimensions.color &&
          v.opt.size.value === this.selectedDimensions.jacket
        )
      })
    },
    selectedPant() {
      return this.shopifyProduct.variants.find((v) => {
        return (
          v.opt.group.label === VARIANT_TYPE.PANT &&
          v.opt.color.value === this.selectedDimensions.color &&
          v.opt.size.value === this.selectedDimensions.pant
        )
      })
    },
    hasJacket() {
      if (this.selectedJacket) {
        return this.selectedJacket.available
      }
      return false
    },
    hasPant() {
      if (this.selectedPant) {
        return this.selectedPant.available
      }
      return false
    },
    soldOut() {
      return !this.hasJacket && !this.hasPant
    },
    orderType() {
      if (this.productStatus === STATUS.PRECOMMANDE) {
        return 'preorder'
      }
      return 'stock'
    },
    showTimer() {
      if (
        this.shopifyProduct.hasDates &&
        this.shopifyProduct.isSaleableAsStock
      ) {
        return this.shouldShowTimer(this.shopifyProduct.endDate)
      }

      if (this.productDocument.dates.end) {
        return this.shouldShowTimer(this.productDocument.dates.end)
      }

      return false
    },
    productHasBeenAccessedFromUpsell() {
      if (process.client) {
        let upsellsId = window.sessionStorage.getItem(SS_UPSELLS_ITEMS)
        if (upsellsId) {
          upsellsId = JSON.parse(upsellsId)
          return (
            upsellsId.includes(this.shopifyProduct.id) ||
            this.$route.query.upsell === 'true'
          )
        }
      }
      return this.$route.query.upsell === 'true'
    },
  },
  created() {
    this.selectFirstAvailableVariant()
    this.numberOfSells = {
      from:
        this.shopifyProduct.totalInventory + this.productDocument.stock_initial,
      to:
        this.shopifyProduct.totalInventory + this.productDocument.stock_initial,
    }
  },
  mounted() {
    this.loading = false

    if (this.productVariantUpdated) {
      this.refresh = true
    } else {
      this.$nuxt.$on(EVENTS.PRODUCT_VARIANT_UPDATED, () => {
        this.$nextTick(() => {
          this.selectFirstAvailableVariant()
          this.numberOfSells.to =
            this.shopifyProduct.totalInventory +
            this.productDocument.stock_initial
          this.refresh = true
        })
      })
    }
  },
  beforeDestroy() {
    this.$nuxt.$off(EVENTS.PRODUCT_VARIANT_UPDATED)
  },
  methods: {
    ...mapActions({
      addItemToCart: 'cart/addItems',
    }),
    /**
     * Permet d'extraire la couleur de la clé couleur d'un productDocument.colors[]_key
     * @param colorKey La clé de la couleur (avec ou sans la 3ème dimension) selon le format "third__color"
     * @returns La couleur correspondante
     */
    getColorFromColorKey(colorKey) {
      return this.hasThirdDimension ? colorKey.split('__')[1] : colorKey
    },
    shouldShowTimer(date) {
      const now = new Date()
      return isPast(date) || differenceInDays(date, now) <= 10
    },
    selectFirstAvailableVariant() {
      if (this.$route.query.jacket || this.$route.query.pant) {
        const variantJacket = this.shopifyProduct.variants.find(
          (v) => v.id === parseInt(this.$route.query.jacket)
        )

        const variantPant = this.shopifyProduct.variants.find(
          (v) => v.id === parseInt(this.$route.query.pant)
        )

        if (variantJacket || variantPant) {
          this.selectedDimensions.color = variantJacket
            ? variantJacket.opt.color.value
            : variantPant.opt.color.value
          this.selectedDimensions.jacket = variantJacket
            ? variantJacket.opt.size.value
            : ''
          this.selectedDimensions.pant = variantPant
            ? variantPant.opt.size.value
            : ''

          this.updateState(false)

          return
        }
      }

      if (this.$route.query.color) {
        if (
          this.shopifyProduct.isDimensionHasValue(
            'color',
            slugify(this.$route.query.color)
          )
        ) {
          const variant = this.shopifyProduct.variants.find(
            (v) => v.opt.color.value === this.$route.query.color
          )

          this.selectedDimensions.color = variant.opt.color.value

          this.updateState(false)
          return
        }
      }

      this.selectFirstAvailableColor()

      this.$nextTick().then((_) => {
        this.selectFirstAvailableJacket()
        this.selectFirstAvailablePant()
      })
      this.updateState(false)
    },
    selectFirstAvailableColor() {
      const firstAvailableColor = this.colorsDimension.find((c) => c.available)
      if (firstAvailableColor) {
        this.selectedDimensions.color = firstAvailableColor.value
      } else {
        this.selectedDimensions.color = this.colorsDimension[0].value
      }
    },
    selectFirstAvailableJacket() {
      if (this.jacketsDimension.length > 0) {
        let firstAvailableJacket = this.jacketsDimension.find(
          (c) => c.available
        )
        if (!firstAvailableJacket) {
          firstAvailableJacket = this.jacketsDimension[0]
        }

        this.selectedDimensions.jacket = firstAvailableJacket.opt.size.value
      }
    },
    selectFirstAvailablePant() {
      if (this.pantsDimension.length > 0) {
        let firstAvailablePant = this.pantsDimension.find((c) => c.available)
        if (!firstAvailablePant) {
          firstAvailablePant = this.pantsDimension[0]
        }

        this.selectedDimensions.pant = firstAvailablePant.opt.size.value
      }
    },
    onColorChange() {
      const jacket = this.jacketsDimension.find(
        (s) => s.opt.size.value === this.selectedDimensions.jacket
      )

      if (jacket) {
        this.selectedDimensions.jacket = jacket.opt.size.value
      } else {
        this.selectFirstAvailableJacket()
      }

      const pant = this.pantsDimension.find(
        (s) => s.opt.size.value === this.selectedDimensions.pant
      )

      if (pant) {
        this.selectedDimensions.pant = pant.opt.size.value
      } else {
        this.selectFirstAvailablePant()
      }

      this.$nextTick().then((_) => {
        this.updateState(true, 'color')
      })
    },
    updateState(fromUserAction, origin) {
      if (!this.selectedJacket && !this.selectedPant) {
        this.$emit(
          'update-color',
          this.selectedDimensions.color,
          fromUserAction
        )
      } else {
        this.$emit(
          'update-color',
          this.selectedJacket
            ? this.selectedJacket.opt.color.value
            : this.selectedPant.opt.color.value,
          fromUserAction
        )
      }

      this.$emit('update-variant', this.selectedJacket, this.selectedPant)

      if (fromUserAction && origin) {
        if (this.selectedJacket && origin === 'jacket') {
          this.$track.selectVariant(
            this.productDocument.title,
            this.shopifyProduct,
            this.selectedJacket,
            this.shopifyProduct.currency
          )
        }

        if (this.selectedPant && origin === 'pant') {
          this.$track.selectVariant(
            this.productDocument.title,
            this.shopifyProduct,
            this.selectedPant,
            this.shopifyProduct.currency
          )
        }
      }
    },
    exchange() {
      this.$emit('show-modal', 'exchange')
    },
    sizeGuide() {
      this.$emit('show-modal', 'size-guide')
    },

    addToCart() {
      const customAttributes = []
      customAttributes.push({ key: '_order_type', value: this.orderType })
      customAttributes.push({
        key: '_delivery',
        value: this.$t(this.deliveryText.ref, this.deliveryText.options),
      })
      customAttributes.push({
        key: '_delivery_last_updated_at',
        value: new Date().toJSON(),
      })
      customAttributes.push({
        key: '_slug',
        value: replaceUids.replaceAlterWithOrigin(
          this.$i18n.locale,
          this.productDocument.uid
        ),
      })
      customAttributes.push({ key: '_locale', value: this.$i18n.locale })
      customAttributes.push({ key: '_basket_date', value: new Date().toJSON() })
      if (this.careGuide !== '') {
        customAttributes.push({
          key: '_guide-entretien',
          value: this.careGuide,
        })
      }
      if (this.delivery && !isNaN(this.delivery.getTime())) {
        customAttributes.push({
          key: '_delivery_date',
          value: this.delivery,
        })
      }

      if (this.productHasBeenAccessedFromUpsell) {
        customAttributes.push({
          key: '_upsell',
          value: 'true',
        })
      }

      const items = []

      if (this.selectedJacket && this.selectedJacket.available) {
        const cartJacketItem = {
          merchandiseId: ShopifyBase64.encode(
            SHOPIFY_TYPES.VARIANT,
            this.selectedJacket.id
          ),
          quantity: 1,
          attributes: customAttributes,
        }
        items.push(cartJacketItem)
      }

      if (this.selectedPant && this.selectedPant.available) {
        const cartPantItem = {
          merchandiseId: ShopifyBase64.encode(
            SHOPIFY_TYPES.VARIANT,
            this.selectedPant.id
          ),
          quantity: 1,
          attributes: customAttributes,
        }
        items.push(cartPantItem)
      }

      this.addItemToCart(items).then((_) => {
        this.$track.addSuitToCart(
          this.productDocument.title,
          this.shopifyProduct,
          this.selectedJacket,
          this.selectedPant,
          this.shopifyProduct.currency,
          this.cart
        )

        document
          .getElementById('linkOpenCart')
          .dispatchEvent(new Event('click'))
      })
    },
  },
}
</script>
